import { CATEGORY, COLOR, COLUMN, COMMITTEE, FORMATTING, JARGON, LAYOUT } from "../../modules/constants"
import { abbreviateMembers } from "../../modules/legends"
import { addPaddingRightToLastCell, calcStats, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { initStats2 } from "../../components/layout/InitStats"

const { border1, left, integer, string } = FORMATTING
const borderLeft = border1
const stats2 = {}

var companyName = ""
var color = {}
var tooltip = ""

// Functions to create tables for the LSE Companies/Committees page
export const createLseCompaniesCommitteesHeadersData = props => {
    // console.log('createCommitteesHeadersData props:', props)
    const { portrait, showLogos, the350club } = props

    const committeeWidth = LAYOUT.COMMITTEE.WIDTH

    const company = headCell({
        align: left,
        colSpan: 2,
        id: COLUMN.COMPANY,
        tooltipTitle: "",
    })

    const ac = headCell({
        borderLeft: portrait ? null : borderLeft,
        id: COLUMN.COMMITTEE.AUDIT.CHAIRS,
        width: committeeWidth,
    })

    const am = headCell({
        id: COLUMN.COMMITTEE.AUDIT.MEMBERS,
        width: committeeWidth,
        label: abbreviateMembers({ committee: COLUMN.COMMITTEE.AUDIT, portrait })
    })

    const rc = headCell({
        borderLeft,
        id: COLUMN.COMMITTEE.REMUNERATION.CHAIRS,
        width: committeeWidth,
    })

    const rm = headCell({
        id: COLUMN.COMMITTEE.REMUNERATION.MEMBERS,
        width: committeeWidth,
    })

    const nc = headCell({
        borderLeft,
        id: COLUMN.COMMITTEE.NOMINATION.CHAIRS,
        width: committeeWidth,
    })

    const nm = headCell({
        id: COLUMN.COMMITTEE.NOMINATION.MEMBERS,
        width: committeeWidth,
    })

    const starHead = headCell({
        align: left,
        id: COLUMN.STAR,
        width: LAYOUT.STAR_WIDTH,
    })

    const logo = headCell({
        align: left,
        id: COLUMN.LOGO,
        width: LAYOUT.LOGO_WIDTH,
    })

    let headCells = portrait
        ? [starHead, company, ac, am]
        : [starHead, company, ac, am, rc, rm, nc, nm]

    if (showLogos)
        headCells.splice(1, 0, logo) // insert logo header

    if (the350club)
        headCells.shift() // Remove starHead

    addPaddingRightToLastCell(headCells)
    // console.log('headCells:', headCells)
    return headCells
}

export const createLseCompaniesCommitteesFooterData = props => {
    // console.log('createLseCompaniesFooterwData props:', props)
    const { portrait, showLogos, the350club } = props
    let column = null
    const tooltip = ""
    const precision = 2

    column = COLUMN.COMPANY
    const suffix = ` ${stats2[column].total === 1 ? JARGON.COMPANY : JARGON.COMPANIES}`

    const company = {
        total: getCell({ align: left, column, suffix, tooltip, type: integer, value: stats2[column].total }),
        mean: getCell({ column })
    }

    for (const property in stats2)
        calcStats(stats2[property])

    column = COLUMN.STAR
    const starFoot = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.LEGEND
    const legend = {
        total: getCell({ column, label: "Total:" }),
        mean: getCell({ column, label: "Average:" }),
    }

    column = COLUMN.COMMITTEE.AUDIT.CHAIRS
    const cac = {
        total: getCell({ borderLeft: portrait ? null : borderLeft, column, type: integer, value: stats2[column].total }),
        mean: getCell({ borderLeft: portrait ? null : borderLeft, column, precision, value: stats2[column].mean })
    }

    column = COLUMN.COMMITTEE.AUDIT.MEMBERS
    const cam = {
        total: getCell({ column, type: integer, value: stats2[column].total }),
        mean: getCell({ column, precision, value: stats2[column].mean })
    }

    column = COLUMN.COMMITTEE.REMUNERATION.CHAIRS
    const crc = {
        total: getCell({ borderLeft, column, type: integer, value: stats2[column].total }),
        mean: getCell({ borderLeft, column, precision, value: stats2[column].mean })
    }

    column = COLUMN.COMMITTEE.REMUNERATION.MEMBERS
    const crm = {
        total: getCell({ column, type: integer, value: stats2[column].total }),
        mean: getCell({ column, precision, value: stats2[column].mean })
    }

    column = COLUMN.COMMITTEE.NOMINATION.CHAIRS
    const cnc = {
        total: getCell({ borderLeft, column, type: integer, value: stats2[column].total }),
        mean: getCell({ borderLeft, column, precision, value: stats2[column].mean })
    }

    column = COLUMN.COMMITTEE.NOMINATION.MEMBERS
    const cnm = {
        total: getCell({ column, type: integer, value: stats2[column].total }),
        mean: getCell({ column, precision, value: stats2[column].mean })
    }

    const footCells = {
        totals: portrait
            ? [starFoot.total, company.total, legend.total, cac.total, cam.total]
            : [starFoot.total, company.total, legend.total, cac.total, cam.total, crc.total, crm.total, cnc.total, cnm.total],
        means: portrait
            ? [starFoot.mean, company.mean, legend.mean, cac.mean, cam.mean]
            : [starFoot.mean, company.mean, legend.mean, cac.mean, cam.mean, crc.mean, crm.mean, cnc.mean, cnm.mean],
    }

    if (showLogos) {
        const logoFoot = headCell({ id: COLUMN.LOGO, align: left })

        footCells.totals.splice(1, 0, logoFoot) // insert logo footer
        footCells.means.splice(1, 0, logoFoot) // insert logo footer
    }

    if (the350club) {
        footCells.totals.shift() // Remove starFoot
        footCells.means.shift()
    }

    addPaddingRightToLastCell(footCells.totals)
    addPaddingRightToLastCell(footCells.means)
    // console.log('footCells:', footCells)
    return footCells
}

//#region Rows
export const createLseCompaniesCommitteesRowData = props => {
    // console.log('createCommitteesRowData props:', props)
    const { items, stats } = props
    let rows = []
    const tooltipTitle = "" // JARGON.MORE_INFO

    initStats2(stats2)
    if (items) {
        items.forEach(item => {
            rows.push(createCommitteesRow({ ...props, item, tooltipTitle }))
            ++stats.numRows
        })
    }
    // console.log('rows:', rows)
    // console.log('stats:',stats)
    return rows
}

var total = 0
// Returns true if the total committee members (inc chair) is 0
const hasZeroMembers = committees => {
    total = 0

    for (let committee = 0; committee < committees.length; committee++) // committees.length is 3 - audit, remuneration and nomination
        for (let position = 0; position < committees[committee].length; position++) // committees[committee].length is 2 - chair or member
            total += committees[committee][position] === -1 // -1 if there is no committee
                ? 0
                : committees[committee][position]

    return total === 0
}

var zeroMembers = false
// Handle case where there is no committee - where value === -1
const getCommitteeProps = (value, committee) => ({
    backgroundColor: value === -1 ? COMMITTEE.NONEXISTANT.COLOR : null,
    sortValue: value === -1 ? 999 : value,
    tooltip: value === -1 ? `${companyName} has no ${committee} Committee` : tooltip,
    value: zeroMembers ? "" : value === -1 ? "" : value,
})

const createCommitteesRow = ({ item, portrait, showLogos }) => {
    const { cmtes: committees } = item
    companyName = item.names[0]
    zeroMembers = hasZeroMembers(committees)
    color = zeroMembers ? COLOR.LIGHT_GREY : COLOR.BLACK
    tooltip = zeroMembers ? JARGON.NO_COMMITTEE_DATA : ""

    const cellSpec = {
        [COLUMN.COMPANY]: {
            value: 1,
        },
        [COLUMN.NAME]: {
            align: left,
            color,
            colSpan: 2,
            tooltip,
            type: string,
            value: companyName,
        },
        [COLUMN.COMMITTEE.AUDIT.CHAIRS]: {
            borderLeft: portrait ? null : borderLeft,
            color,
            ...getCommitteeProps(committees[0][0], COMMITTEE.AUDIT)
        },
        [COLUMN.COMMITTEE.AUDIT.MEMBERS]: {
            color,
            ...getCommitteeProps(committees[0][1], COMMITTEE.AUDIT)
        },
        [COLUMN.COMMITTEE.REMUNERATION.CHAIRS]: {
            borderLeft,
            color,
            ...getCommitteeProps(committees[1][0], COMMITTEE.REMCOM),
        },
        [COLUMN.COMMITTEE.REMUNERATION.MEMBERS]: {
            color,
            ...getCommitteeProps(committees[1][1], COMMITTEE.REMCOM),
        },
        [COLUMN.COMMITTEE.NOMINATION.CHAIRS]: {
            color,
            borderLeft,
            ...getCommitteeProps(committees[2][0], COMMITTEE.NOMCOM),
        },
        [COLUMN.COMMITTEE.NOMINATION.MEMBERS]: {
            color,
            ...getCommitteeProps(committees[2][1], COMMITTEE.NOMCOM),
        },
    }

    // NB: 'setStats()' accepts both a number or bool for its 1st parameter
    // since the unary '+' operator converts boolean into a number
    setStats(cellSpec[COLUMN.COMPANY].value, stats2[COLUMN.COMPANY], true)
    // Use !zeroMembers so that we set numOf where a value is 0, but not all committee values are 0
    setStats(cellSpec[COLUMN.COMMITTEE.AUDIT.CHAIRS].value, stats2[COLUMN.COMMITTEE.AUDIT.CHAIRS], !zeroMembers)
    setStats(cellSpec[COLUMN.COMMITTEE.AUDIT.MEMBERS].value, stats2[COLUMN.COMMITTEE.AUDIT.MEMBERS], !zeroMembers)

    setStats(cellSpec[COLUMN.COMMITTEE.REMUNERATION.CHAIRS].value, stats2[COLUMN.COMMITTEE.REMUNERATION.CHAIRS], !zeroMembers)
    setStats(cellSpec[COLUMN.COMMITTEE.REMUNERATION.MEMBERS].value, stats2[COLUMN.COMMITTEE.REMUNERATION.MEMBERS], !zeroMembers)

    setStats(cellSpec[COLUMN.COMMITTEE.NOMINATION.CHAIRS].value, stats2[COLUMN.COMMITTEE.NOMINATION.CHAIRS], !zeroMembers)
    setStats(cellSpec[COLUMN.COMMITTEE.NOMINATION.MEMBERS].value, stats2[COLUMN.COMMITTEE.NOMINATION.MEMBERS], !zeroMembers)

    const name = getCellWithTooltip(cellSpec[COLUMN.NAME])
    const ac = getCellWithTooltip(cellSpec[COLUMN.COMMITTEE.AUDIT.CHAIRS])
    const am = getCellWithTooltip(cellSpec[COLUMN.COMMITTEE.AUDIT.MEMBERS])

    const rc = getCellWithTooltip(cellSpec[COLUMN.COMMITTEE.REMUNERATION.CHAIRS])
    const rm = getCellWithTooltip(cellSpec[COLUMN.COMMITTEE.REMUNERATION.MEMBERS])

    const nc = getCellWithTooltip(cellSpec[COLUMN.COMMITTEE.NOMINATION.CHAIRS])
    const nm = getCellWithTooltip(cellSpec[COLUMN.COMMITTEE.NOMINATION.MEMBERS])

    const logo = item.logo
        ? item.logo
        : "no logo"

    const cellWithTooltips = portrait
        ? [name, ac, am] // portrait
        : [name, ac, am, rc, rm, nc, nm]

    addPaddingRightToLastCell(cellWithTooltips)

    return {
        _id: item.ids[0],
        category: CATEGORY.COMPANY,
        cellWithTooltips,
        iconColor: COLOR.GOOD,
        ids: item.ids,
        index: item.index,
        isFavourite: item.isFavourite,
        link: `/company/${item.ids[0]}`,
        logo: showLogos ? logo : null, // insert logo
        title: item.companyName,
    }
}
//#endregion Rows

